import { Check, User } from 'lucide-react';

import { cn } from '@/lib/shadcn/utils';
import type { ActivityStep } from '@/modules/hiring-manager/utils/jobActivity';
import { TooltipContent } from '@/modules/ui/components/tooltip/Tooltip';

interface JobStepsContentProps {
  /** Lets the element that opens the steps point at them while they are open. */
  id?: string;
  title: string;
  steps: ActivityStep[];
}

/**
 * A job's steps and where each stands, as the content of a dark tooltip.
 * Shared by the Home job rows and the job page's Finlay section.
 */
export function JobStepsContent({ id, title, steps }: JobStepsContentProps) {
  return (
    <TooltipContent
      className="flex w-80 flex-col gap-2 px-3 py-2.5 text-left text-wrap"
      id={id}
      side="bottom"
      variant="dark"
    >
      <span className="font-semibold">{title}</span>
      {steps.map((step) => (
        <span className="flex gap-2" key={step.name}>
          <span className="flex h-4 w-3 shrink-0 items-center justify-center">
            <StepMark state={step.state} />
          </span>
          <span
            className={cn(
              'flex flex-col gap-px',
              step.state !== 'running' &&
                step.state !== 'blocked' &&
                'opacity-70',
            )}
          >
            <span className="font-medium">{step.name}</span>
            <span className="leading-[1.4] opacity-80">{step.detail}</span>
          </span>
        </span>
      ))}
    </TooltipContent>
  );
}

function StepMark({ state }: { state: ActivityStep['state'] }) {
  switch (state) {
    case 'done':
      return <Check className="size-[11px] opacity-70" />;
    case 'you':
      return <User className="size-[11px] opacity-70" />;
    case 'running':
      return <span className="bg-primary size-1.5 rounded-full" />;
    case 'blocked':
      return <span className="size-1.5 rounded-full bg-amber-500" />;
    case 'idle':
      return <span className="bg-background/30 size-1.5 rounded-full" />;
  }
}
